import React, { useState } from 'react';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (issueText: string) => Promise<void>;
  problemTitle: string;
}

const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  problemTitle,
}) => {
  const [issueText, setIssueText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!issueText.trim()) {
      setError('Please describe the issue you are experiencing.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(issueText.trim());
      setShowSuccess(true);
      setIssueText('');
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'Failed to submit report'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setIssueText('');
      setError(null);
      setShowSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
      <div className='bg-white rounded-2xl shadow-xl max-w-md w-full p-6'>
        {showSuccess ? (
          // Success State
          <div className='text-center'>
            <div className='w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center'>
              <svg
                className='w-8 h-8 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M5 13l4 4L19 7'
                />
              </svg>
            </div>
            <h3 className='text-2xl font-bold text-gray-900 mb-2'>
              Report Submitted!
            </h3>
            <p className='text-gray-600 mb-6'>
              Thank you for your feedback. We'll review the issue and work on
              improvements.
            </p>
            <button
              onClick={handleClose}
              className='w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors'
            >
              Close
            </button>
          </div>
        ) : (
          // Form State
          <>
            <div className='text-center mb-6'>
              <div className='w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center'>
                <svg
                  className='w-8 h-8 text-orange-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z'
                  />
                </svg>
              </div>
              <h3 className='text-2xl font-bold text-gray-900 mb-2'>
                Report an Issue
              </h3>
              <p className='text-gray-600 text-sm'>
                Problem: <span className='font-medium'>{problemTitle}</span>
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className='space-y-4'
            >
              <div>
                <label
                  htmlFor='issue-text'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  Describe the issue you're experiencing:
                </label>
                <textarea
                  id='issue-text'
                  value={issueText}
                  onChange={(e) => setIssueText(e.target.value)}
                  className='w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none'
                  placeholder='Please describe the issue in detail. For example: AI responses are not relevant, problem statement is unclear, technical difficulties, etc.'
                  disabled={isSubmitting}
                  maxLength={1000}
                />
                <div className='text-xs text-gray-500 mt-1'>
                  {issueText.length}/1000 characters
                </div>
              </div>

              {error && (
                <div className='text-sm text-red-600 bg-red-50 p-3 rounded-lg'>
                  {error}
                </div>
              )}

              <div className='flex space-x-3 pt-4'>
                <button
                  type='button'
                  onClick={handleClose}
                  className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors'
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  disabled={isSubmitting || !issueText.trim()}
                >
                  {isSubmitting ? (
                    <span className='flex items-center justify-center'>
                      <svg
                        className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        ></circle>
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        ></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Report'
                  )}
                </button>
              </div>
            </form>

            <div className='mt-4 p-3 bg-blue-50 rounded-lg'>
              <p className='text-xs text-blue-700'>
                <strong>Note:</strong> Your report will include problem details,
                session information, and current code to help us investigate the
                issue.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportModal;
